import { ClientTabType, ClientType, ClientTypeEnum, PAGES } from "@dune/types";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	PaginatedDataTable,
	Tabs,
	TabsList,
	TabsTriggerWithNumber,
	useDebounce,
	useSidebar,
} from "@dune/ui";
import { getClientId, getClientTypeLabel } from "@dune/utils";
import { ChevronDown, SearchSm } from "@untitled-ui/icons-react";
import { FileWarning } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	useGetClientsList,
	useGetClientsCounts,
} from "../../../api/queries/clients.queries";
import { useMe } from "../../../api/queries/users.queries";
import PageLayoutWithTabs from "../../../component/layout/PageLayoutWithTabs";
import { getBrokerColumns, getDuneColumns } from "./ClientColumns";
import { usePagination } from "../../../hooks/usePagination";
import { RoleEnum } from "../../../../../../packages/vars/src";

const ClientsListPage = () => {
	const [searchInput, setSearchInput] = useState("");
	const [selectedTab, setSelectedTab] = useState<ClientTabType>(
		ClientTabType.ALL,
	);
	const [typeFilter, setTypeFilter] = useState<ClientType | undefined>(
		undefined,
	);
	const { page, limit, setPage, handleLimitChange } = usePagination();

	const navigate = useNavigate();
	const { me: user } = useMe();
	const debouncedSearch = useDebounce(searchInput, 1000);
	const isDuneUser = user?.role === RoleEnum.DUNE;
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	const mapTabToStatus = (tab: ClientTabType): string | undefined => {
		switch (tab) {
			case ClientTabType.ALL:
				return undefined;
			case ClientTabType.NEW:
				return "NEW";
			case ClientTabType.IN_PROGRESS:
				return "IN_PROGRESS";
			case ClientTabType.CANCELLED:
				return "CANCELLED";
			case ClientTabType.VALIDATED:
				return "VALIDATED";
			default:
				return undefined;
		}
	};

	const statusFilter = mapTabToStatus(selectedTab);
	const {
		data: filteredClientsResponse,
		error: filteredError,
		isLoading: filteredLoading,
	} = useGetClientsList({
		search: debouncedSearch,
		status: statusFilter,
		type: typeFilter,
		page,
		limit,
	});

	const filteredClients = filteredClientsResponse?.data || [];
	const paginationMeta = filteredClientsResponse?.meta || {
		page,
		limit,
		total: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	const { data: clientsCounts } = useGetClientsCounts({
		search: debouncedSearch,
		type: typeFilter,
	});

	const getTabCount = (tab: ClientTabType): number => {
		if (!clientsCounts) return 0;
		switch (tab) {
			case ClientTabType.ALL:
				return clientsCounts.ALL;
			case ClientTabType.NEW:
				return clientsCounts.NEW;
			case ClientTabType.IN_PROGRESS:
				return clientsCounts.IN_PROGRESS;
			case ClientTabType.CANCELLED:
				return clientsCounts.CANCELLED;
			case ClientTabType.VALIDATED:
				return clientsCounts.VALIDATED;
			default:
				return 0;
		}
	};

	const handleTabChange = (value: string) => {
		setSelectedTab(value as ClientTabType);
		setPage(1);
	};

	const handleTypeFilterChange = (value: ClientType | undefined) => {
		setTypeFilter(value);
		setPage(1);
	};

	const columns = isDuneUser ? getDuneColumns() : getBrokerColumns();

	if (filteredError) {
		return (
			<PageLayoutWithTabs
				title={isDuneUser ? "Clients finaux" : "Clients"}
				tabContent={{}}
			>
				<div className="space-y-4">
					<div className="text-lg font-semibold text-red-600">
						Erreur de chargement des clients
					</div>
					<div className="text-sm text-red-500">{filteredError?.message}</div>
				</div>
			</PageLayoutWithTabs>
		);
	}

	return (
		<PageLayoutWithTabs
			title={isDuneUser ? "Clients finaux" : "Clients"}
			tabContent={{}}
		>
			<div className="space-y-6 flex-1 min-h-0 flex flex-col">
				{/* Status Tabs for Dune Users */}
				{isDuneUser && (
					<Tabs value={selectedTab} onValueChange={handleTabChange}>
						<TabsList className="grid w-full grid-cols-5">
							<TabsTriggerWithNumber
								value={ClientTabType.ALL}
								number={paginationMeta.total}
								variant="compact"
								className="flex gap-2"
							>
								Tous
							</TabsTriggerWithNumber>
							<TabsTriggerWithNumber
								value={ClientTabType.NEW}
								number={getTabCount(ClientTabType.NEW)}
								variant="compact"
								className="flex gap-2"
							>
								Nouveau
							</TabsTriggerWithNumber>
							<TabsTriggerWithNumber
								value={ClientTabType.IN_PROGRESS}
								number={getTabCount(ClientTabType.IN_PROGRESS)}
								variant="compact"
								className="flex gap-2"
							>
								En cours
							</TabsTriggerWithNumber>
							<TabsTriggerWithNumber
								value={ClientTabType.CANCELLED}
								number={getTabCount(ClientTabType.CANCELLED)}
								variant="compact"
								className="flex gap-2"
							>
								Annulé
							</TabsTriggerWithNumber>
							<TabsTriggerWithNumber
								value={ClientTabType.VALIDATED}
								number={getTabCount(ClientTabType.VALIDATED)}
								variant="compact"
								className="flex gap-2"
							>
								Validé
							</TabsTriggerWithNumber>
						</TabsList>
					</Tabs>
				)}

				{/* Filters */}
				<div className="flex flex-row gap-2 w-full">
					<div className="w-1/2 flex flex-row gap-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<div className="w-full bg-white">
									<Input
										type="text"
										readOnly
										placeholder="Type"
										className="cursor-pointer"
										value={
											typeFilter
												? getClientTypeLabel(typeFilter)
												: "Tous les types"
										}
										iconOrText={<ChevronDown />}
										iconOrTextPosition="right"
									/>
								</div>
							</DropdownMenuTrigger>
							<DropdownMenuContent className="min-w-[200px]">
								<DropdownMenuItem
									onClick={() => handleTypeFilterChange(undefined)}
								>
									Tous les types
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => handleTypeFilterChange(ClientTypeEnum.USER)}
								>
									{getClientTypeLabel(ClientTypeEnum.USER)}
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => handleTypeFilterChange(ClientTypeEnum.SOCIETY)}
								>
									{getClientTypeLabel(ClientTypeEnum.SOCIETY)}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>

					<div className="w-1/2 bg-white">
						<Input
							type="text"
							placeholder="Recherche rapide"
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							iconOrText={<SearchSm />}
							iconOrTextPosition="right"
						/>
					</div>
				</div>

				{/* Table */}
				<div className="flex-1 min-h-0 w-full overflow-auto">
					<div className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}>
						<PaginatedDataTable
							columns={columns}
							data={filteredClients}
							meta={paginationMeta}
							onPageChange={setPage}
							onLimitChange={handleLimitChange}
							isLoading={filteredLoading}
							noResultsText="Aucun client trouvé"
							icon={FileWarning}
							variant="default"
							onRowClick={(row) => {
								const clientId = getClientId(row);
								const clientType = row.clientType;
								const url = clientType
									? `${PAGES.CLIENTS.show.withParams(clientId)}?clientType=${clientType}`
									: PAGES.CLIENTS.show.withParams(clientId);
								navigate(url);
							}}
						/>
					</div>
				</div>
			</div>
		</PageLayoutWithTabs>
	);
};

export default ClientsListPage;
